import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'

import { initialState } from '../SearchForm/utils'
import type { HOTEL_FORM_STATE_TYPE, HOTEL_FORM_SUBMIT_TYPE } from '../types'
import type { MappedSearchFilter } from './aiSearchMap'
import { getRetainedApiFilters, getRetainedPriceRange } from './aiSearchMap'
import { logAiSearchPennyRedirect, logAiSearchResolveFailed } from './logAiSearch'

export type AiSearchSubmitParams = {
  query: string
  startDate: string
  endDate: string
  travelers: TravelerState
  city: LOCATION_SEARCH_TYPE | null
  filters: MappedSearchFilter[]
  priceRange: { min: number | null; max: number | null }
  removedFilterIds: ReadonlySet<string>
  isPennyEligible: boolean
}

export function buildPennyAskUrl(query: string) {
  return `/penny?askPrompt=${encodeURIComponent(query)}`
}

export function openPennyAskPrompt(query: string) {
  logAiSearchPennyRedirect(query)
  window.open(buildPennyAskUrl(query), '_blank', 'noopener,noreferrer')
}

export function toHotelSubmitFromInterpretation({
  startDate,
  endDate,
  travelers,
  city,
  filters,
  priceRange,
  removedFilterIds,
  baseValues,
}: Omit<AiSearchSubmitParams, 'query' | 'isPennyEligible'> & {
  baseValues: HOTEL_FORM_STATE_TYPE
}): HOTEL_FORM_SUBMIT_TYPE {
  const retainedPrice = getRetainedPriceRange(priceRange, removedFilterIds)
  const retainedFilters = getRetainedApiFilters(filters, removedFilterIds)

  return {
    ...baseValues,
    endLocation: city
      ? {
          ...city,
          filters: retainedFilters,
        }
      : null,
    startDate,
    endDate,
    travelers,
    selectedTopFilters: initialState.selectedTopFilters,
    minPrice: retainedPrice.min,
    maxPrice: retainedPrice.max,
  }
}

export function submitAiInterpretedSearch(
  params: AiSearchSubmitParams,
  onHotelSubmit: (values: HOTEL_FORM_SUBMIT_TYPE) => void,
  baseValues: HOTEL_FORM_STATE_TYPE,
) {
  if (params.isPennyEligible) {
    openPennyAskPrompt(params.query)
    return
  }

  if (!params.city) {
    logAiSearchResolveFailed(params.query)
    throw new Error('AI_SEARCH_NO_MATCH')
  }

  onHotelSubmit(toHotelSubmitFromInterpretation({ ...params, baseValues }))
}
